/**
 * Pure helpers for the Kaption "New message" (new conversation) modal.
 *
 * Everything here is framework-free and side-effect-free so it can be unit
 * tested in isolation (see spec/newConversationHelpers.spec.js). The Vue
 * components import these helpers for recipient parsing, template gallery
 * filtering, variable extraction, smart defaults, validation and payload
 * building.
 */
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

export const TEMPLATE_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const DATE_TOKEN_RE =
  /(date|day|fecha|data|datum|jour|when|schedul|appt|appointment)/i;
const NAME_TOKEN_RE = /(name|nombre|nome|first[_\s-]?name)/i;

/**
 * Detect whether the user is typing a raw phone number rather than a name.
 * We accept a leading "+" but do NOT require it. A value counts as a number
 * as soon as it contains one digit, so formatting and contact search can start
 * on the first keystroke.
 */
export const looksLikePhoneNumber = input => {
  if (!input) return false;
  const value = String(input).trim();
  if (!/^[+]?[\d\s().-]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 1;
};

/**
 * Derive the default country (ISO-2) to use when the user types a local
 * number without a country code. We read it from the WhatsApp inbox's own
 * phone number. Falls back to `fallback` (default 'US').
 */
export const deriveDefaultCountry = (inbox, fallback = 'US') => {
  const raw =
    inbox?.phoneNumber || inbox?.phone_number || inbox?.messagingServiceSid;
  if (raw) {
    const parsed = parsePhoneNumberFromString(String(raw));
    if (parsed?.country) return parsed.country;
  }
  return fallback;
};

/**
 * Normalise a raw user-typed recipient into an E.164 number.
 * - A leading "+" is honoured and parsed internationally.
 * - "00" international prefix is supported.
 * - Otherwise the number is parsed against `defaultCountry` (from the inbox).
 *
 * Returns `{ e164, isValid, national }`. `isValid` gates create-on-send.
 */
export const normalizeToE164 = (input, defaultCountry = 'US') => {
  const value = String(input || '').trim();
  if (!value) return { e164: '', isValid: false, national: '' };

  let parsed;
  if (value.startsWith('+')) {
    parsed = parsePhoneNumberFromString(value);
  } else if (value.startsWith('00')) {
    parsed = parsePhoneNumberFromString(`+${value.slice(2)}`);
  } else if (defaultCountry === 'AR') {
    // Argentine WhatsApp identifiers require the mobile token `9` after +54.
    // Local users conventionally type `11...`, not `+54 9 11...`.
    // new-conversation-modal.RECIPIENT.1
    const local = value
      .replace(/\D/g, '')
      .replace(/^0/, '')
      .replace(/^(\d{2,4})15/, '$1');
    parsed = parsePhoneNumberFromString(`+549${local}`);
  } else {
    parsed = parsePhoneNumberFromString(value, defaultCountry);
  }

  if (parsed && parsed.isValid()) {
    return {
      e164: parsed.number,
      isValid: true,
      national: parsed.formatNational(),
    };
  }

  // Best-effort fallback so the preview/label still shows something while the
  // user keeps typing; isValid stays false so Send remains blocked.
  const digits = value.replace(/\D/g, '');
  const e164 = value.startsWith('+') ? `+${digits}` : `+${digits}`;
  return { e164, isValid: false, national: value };
};

export const formatPhoneAsYouType = (input, defaultCountry = 'US') => {
  const value = String(input || '');
  if (!value) return '';
  const country =
    value.startsWith('+') || value.startsWith('00')
      ? undefined
      : defaultCountry;
  return new AsYouType(country).input(
    value.startsWith('00') ? `+${value.slice(2)}` : value
  );
};

/* ------------------------------------------------------------------ *
 * Template gallery                                                     *
 * ------------------------------------------------------------------ */

const bodyText = template => {
  const body = template?.components?.find(c => c.type === 'BODY');
  return body?.text || '';
};

/** True for internal Kaption-reserved templates we hide from the gallery. */
export const isInternalTemplate = template =>
  Boolean(template?.name && /^kaption_/i.test(template.name));

/* ------------------------------------------------------------------ *
 * Rumo templates (template-gallery-rumo.*)                            *
 * ------------------------------------------------------------------ */

// kaption_v2_reminder_r4_att1_es, kaption_v23_followup_r1_pt_br …
const RUMO_TEMPLATE_RE =
  /^kaption_(v\d+)_(reminder|followup|scheduled)_r(\d+)(?:_att(\d+))?_[a-z]{2}(?:_[a-z]{2})?$/i;
export const RUMO_TYPES = ['reminder', 'followup', 'scheduled'];

/** { version, type, variant, attempt } for a Rumo template name, else null. */
export const parseRumoTemplate = template => {
  const match = RUMO_TEMPLATE_RE.exec(template?.name || '');
  if (!match) return null;
  return {
    version: match[1].toLowerCase(),
    type: match[2].toLowerCase(),
    variant: Number(match[3]),
    attempt: match[4] ? Number(match[4]) : 0,
  };
};

/**
 * template-gallery-rumo.LIST.1 — a Rumo template of the inbox's own version.
 * Other versions, 2FA and bugfix templates stay internal (hidden).
 */
export const isRumoTemplate = (template, version) => {
  const parsed = parseRumoTemplate(template);
  return Boolean(parsed && (!version || parsed.version === version));
};

/** The highest Rumo version present, used when the inbox has none stored. */
export const latestRumoVersion = (templates = []) => {
  const versions = templates
    .map(parseRumoTemplate)
    .filter(Boolean)
    .map(parsed => Number(parsed.version.slice(1)));
  return versions.length ? `v${Math.max(...versions)}` : null;
};

// 'pt_BR' / 'pt_br' / 'es_AR' → 'pt_BR' / 'es'
const normalizeRumoLanguage = code => {
  if (!code) return '';
  const base = String(code).replace('-', '_').split('_')[0].toLowerCase();
  return base === 'pt' ? 'pt_BR' : base;
};

const rumoSortKey = template => {
  const parsed = parseRumoTemplate(template);
  return [RUMO_TYPES.indexOf(parsed.type), parsed.variant, parsed.attempt];
};
const compareKeys = (a, b) => {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
};

/**
 * template-gallery-rumo.LIST.3 — retry attempts (att1, att2 …) of the same
 * wording are one choice: keep the lowest attempt per type/variant/language.
 */
const dedupeRumoAttempts = templates => {
  const seen = new Map();
  templates.forEach(template => {
    const parsed = parseRumoTemplate(template);
    const key = `${parsed.type}-${parsed.variant}-${normalizeRumoLanguage(template.language)}`;
    const current = seen.get(key);
    if (!current || parsed.attempt < parseRumoTemplate(current).attempt) {
      seen.set(key, template);
    }
  });
  return templates.filter(template => [...seen.values()].includes(template));
};

/** Stable favorite key for a template (name + language). */
export const templateKey = template =>
  `${template?.name || ''}::${template?.language || ''}`;

/**
 * Filter the approved template list for the gallery.
 * @param {Array}  templates raw approved template objects
 * @param {Object} opts { query, category, language, status, hideInternal,
 *   source: 'ALL' | 'RUMO', rumo: { version, language } | null }
 *
 * template-gallery-rumo.LIST.2 — with `rumo` set, the inbox's Rumo templates
 * are listed too, only in one language: the language filter when one is
 * picked, else the active language. `source: 'RUMO'` lists only them.
 */
export const filterTemplates = (templates = [], opts = {}) => {
  const {
    query = '',
    category = 'ALL',
    language = 'ALL',
    status = 'ALL',
    hideInternal = true,
    source = 'ALL',
    rumo = null,
  } = opts;
  const needle = String(query).trim().toLowerCase();
  const rumoLanguage = normalizeRumoLanguage(
    language !== 'ALL' ? language : rumo?.language
  );

  const result = templates.filter(template => {
    if (!template) return false;
    const isRumo = Boolean(rumo) && isRumoTemplate(template, rumo.version);
    if (isRumo) {
      if (normalizeRumoLanguage(template.language) !== rumoLanguage) {
        return false;
      }
    } else {
      if (source === 'RUMO') return false;
      if (hideInternal && isInternalTemplate(template)) return false;
      if (language !== 'ALL' && (template.language || '') !== language) {
        return false;
      }
    }

    if (category !== 'ALL') {
      const tplCategory = (template.category || 'UTILITY').toUpperCase();
      if (tplCategory !== String(category).toUpperCase()) return false;
    }

    if (
      status !== 'ALL' &&
      String(template.status || '').toUpperCase() !== status
    )
      return false;

    if (needle) {
      const haystack =
        `${template.name || ''} ${bodyText(template)}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }

    return true;
  });

  if (!rumo) return result;
  const rumoKept = dedupeRumoAttempts(
    result.filter(template => isRumoTemplate(template, rumo.version))
  );
  return result.filter(
    template =>
      !isRumoTemplate(template, rumo.version) || rumoKept.includes(template)
  );
};

/**
 * template-gallery-rumo.LIST.4 / FAVORITES.2 — gallery sections, in order:
 * favorites (any source), the business's own templates, then Rumo templates
 * sorted reminder → follow-up → scheduled and by variant. Empty sections
 * are dropped.
 */
export const groupGalleryTemplates = (
  templates = [],
  { favorites = [], rumoVersion = null } = {}
) => {
  const favoriteKeys = new Set(favorites);
  const isFavorite = template => favoriteKeys.has(templateKey(template));
  const isRumo = template =>
    Boolean(rumoVersion) && isRumoTemplate(template, rumoVersion);
  const byRumoOrder = (a, b) => compareKeys(rumoSortKey(a), rumoSortKey(b));
  const ordered = list => [
    ...list.filter(template => !isRumo(template)),
    ...list.filter(isRumo).sort(byRumoOrder),
  ];

  return [
    {
      key: 'FAVORITES',
      items: ordered(templates.filter(isFavorite)),
    },
    {
      key: 'OWN',
      items: templates.filter(t => !isFavorite(t) && !isRumo(t)),
    },
    {
      key: 'RUMO',
      items: templates
        .filter(t => !isFavorite(t) && isRumo(t))
        .sort(byRumoOrder),
    },
  ].filter(section => section.items.length);
};

const visibleForPills = (template, rumo) =>
  Boolean(template) &&
  (!isInternalTemplate(template) ||
    (Boolean(rumo) && isRumoTemplate(template, rumo.version)));

/** Unique category tabs present in the given templates (ordered canonically). */
export const getAvailableCategories = (templates = [], rumo = null) => {
  const present = new Set(
    templates
      .filter(t => visibleForPills(t, rumo))
      .map(t => (t.category || 'UTILITY').toUpperCase())
  );
  return TEMPLATE_CATEGORIES.filter(c => present.has(c));
};

/**
 * Unique language codes present in the given templates. With `rumo`, the
 * account's Rumo languages are offered too (template-gallery-rumo.LANG.2).
 */
export const getAvailableLanguages = (templates = [], rumo = null) => {
  const present = new Set(
    templates
      .filter(t => t && !isInternalTemplate(t))
      .map(t => t.language)
      .filter(Boolean)
  );
  (rumo?.languages || []).forEach(code => present.add(code));
  return Array.from(present).sort();
};

/* ------------------------------------------------------------------ *
 * Variables + header                                                  *
 * ------------------------------------------------------------------ */

/** The HEADER component (if any). */
export const getHeaderComponent = template =>
  template?.components?.find(c => c.type === 'HEADER') || null;

/** True when the template has an IMAGE/VIDEO/DOCUMENT header needing an upload. */
export const hasMediaHeader = template => {
  const header = getHeaderComponent(template);
  return Boolean(header && MEDIA_HEADER_FORMATS.includes(header.format));
};

/** Lowercased media type ('image'|'video'|'document') or null. */
export const mediaHeaderType = template => {
  const header = getHeaderComponent(template);
  if (!header || !MEDIA_HEADER_FORMATS.includes(header.format)) return null;
  return header.format.toLowerCase();
};

/** TEXT header string (for non-media headers), or ''. */
export const getHeaderText = template => {
  const header = getHeaderComponent(template);
  return header && header.format === 'TEXT' ? header.text || '' : '';
};

/** FOOTER text, or ''. */
export const getTemplateFooter = template => {
  const footer = template?.components?.find(c => c.type === 'FOOTER');
  return footer?.text || '';
};

const VARIABLE_RE = /{{\s*([^}]+?)\s*}}/g;

const uniqueVariables = text => {
  const seen = new Set();
  const vars = [];
  (String(text || '').match(VARIABLE_RE) || []).forEach(token => {
    const key = token.replace(/{{\s*|\s*}}/g, '').trim();
    if (!seen.has(key)) {
      seen.add(key);
      vars.push({ token, key });
    }
  });
  return vars;
};

/**
 * reply-template-picker.TYPES.1 — variables of a TEXT header
 * (`{{1}}` or `{{name}}`), `[{ token, key }]`. Meta allows at most one.
 */
export const parseHeaderVariables = template =>
  uniqueVariables(getHeaderText(template));

/**
 * reply-template-picker.TYPES.2 — the media Meta keeps as the template's
 * example (a signed CDN link refreshed by the template sync), or ''.
 */
export const getTemplateExampleMediaUrl = template => {
  if (!hasMediaHeader(template)) return '';
  const example = getHeaderComponent(template)?.example || {};
  return example.header_handle?.[0] || example.header_url?.[0] || '';
};

/** The header media the editor starts with: the template's own example, if any. */
export const buildInitialHeaderMedia = template => {
  const url = getTemplateExampleMediaUrl(template);
  const type = mediaHeaderType(template);
  return {
    media_url: url,
    media_type: type,
    // WhatsApp shows a document's filename; start from the template name.
    media_name:
      type === 'document' && template?.name ? `${template.name}.pdf` : '',
    source: url ? 'template_example' : '',
    attachment_hash: '',
    // Display only (the uploaded file's own name); never sent to Meta.
    file_name: '',
  };
};

const SENDABLE_BUTTON_TYPES = [
  'QUICK_REPLY',
  'URL',
  'PHONE_NUMBER',
  'COPY_CODE',
];
const SENDABLE_HEADER_FORMATS = ['TEXT', ...MEDIA_HEADER_FORMATS];
const BUTTON_REASONS = {
  FLOW: 'FLOW',
  CATALOG: 'CATALOG',
  MPM: 'CATALOG',
  SPM: 'CATALOG',
  OTP: 'AUTHENTICATION',
  ORDER_DETAILS: 'ORDER_DETAILS',
};

/**
 * reply-template-picker.TYPES.6 — whether an agent can fill and send this
 * template from the reply box or the new-message wizard. Shapes that need
 * inputs we do not have (carousel cards, catalog products, flows, one-time
 * passwords, location headers, offer expirations) come back
 * `{ supported: false, reason }` so the gallery can disable them instead of
 * sending a payload Meta rejects.
 */
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: `allowFlow` (the
// KAPTION_WHATSAPP_FLOWS flag) makes FLOW-button templates sendable; the
// sender attaches a signed per-recipient flow token.
export const getTemplateSupport = (template, { allowFlow = false } = {}) => {
  const unsupported = reason => ({ supported: false, reason });
  if (!template) return unsupported('UNKNOWN');
  if (String(template.category || '').toUpperCase() === 'AUTHENTICATION')
    return unsupported('AUTHENTICATION');

  const components = template.components || [];
  const types = components.map(c => String(c.type || '').toUpperCase());
  if (types.includes('CAROUSEL')) return unsupported('CAROUSEL');
  if (types.includes('LIMITED_TIME_OFFER'))
    return unsupported('LIMITED_TIME_OFFER');

  const header = getHeaderComponent(template);
  if (header) {
    const format = String(header.format || '').toUpperCase();
    if (format === 'LOCATION') return unsupported('LOCATION');
    if (!SENDABLE_HEADER_FORMATS.includes(format)) return unsupported('HEADER');
    if (format === 'TEXT' && parseHeaderVariables(template).length > 1)
      return unsupported('HEADER');
  }

  const buttons = components.find(c => c.type === 'BUTTONS')?.buttons || [];
  const sendable = allowFlow
    ? [...SENDABLE_BUTTON_TYPES, 'FLOW']
    : SENDABLE_BUTTON_TYPES;
  const odd = buttons.find(
    b => !sendable.includes(String(b.type || '').toUpperCase())
  );
  if (odd)
    return unsupported(
      BUTTON_REASONS[String(odd.type || '').toUpperCase()] || 'BUTTON'
    );
  return { supported: true, reason: null };
};

export const isTemplateSendable = (template, options = {}) =>
  getTemplateSupport(template, options).supported;

/**
 * Parse a template's BUTTONS into a render/validation-friendly list.
 * Each entry: { index, type, text, url, phone_number, needsParam, paramKind }
 *  - QUICK_REPLY / PHONE_NUMBER / static URL -> needsParam:false
 *  - URL containing {{ }}                      -> needsParam:true,  paramKind:'url'
 *  - COPY_CODE                                 -> needsParam:true,  paramKind:'copy_code'
 */
export const parseTemplateButtons = template => {
  const comp = template?.components?.find(c => c.type === 'BUTTONS');
  if (!comp || !Array.isArray(comp.buttons)) return [];
  return comp.buttons.map((btn, index) => {
    const type = btn.type || 'QUICK_REPLY';
    const isDynamicUrl = type === 'URL' && !!btn.url && btn.url.includes('{{');
    const isCopyCode = type === 'COPY_CODE';
    let paramKind = null;
    if (isDynamicUrl) paramKind = 'url';
    else if (isCopyCode) paramKind = 'copy_code';
    return {
      index,
      type,
      text: btn.text || btn.title || btn.label || '',
      url: btn.url || '',
      phone_number: btn.phone_number || '',
      needsParam: isDynamicUrl || isCopyCode,
      paramKind,
      // The fixed part of a dynamic URL, shown before the agent's suffix.
      urlPrefix: isDynamicUrl ? btn.url.split('{{')[0] : '',
      example: (btn.example && btn.example[0]) || '',
      variables: isDynamicUrl
        ? (btn.url.match(/{{\s*([^}]+?)\s*}}/g) || []).map(v =>
            v.replace(/{{\s*|\s*}}/g, '').trim()
          )
        : [],
    };
  });
};

/** Buttons in this template that require a user-supplied parameter. */
export const getButtonsNeedingParam = template =>
  parseTemplateButtons(template).filter(b => b.needsParam);

/**
 * Extract ordered, de-duplicated body variables from a template.
 * Returns `[{ token: '{{1}}', key: '1' }, ...]`.
 */
export const parseBodyVariables = template =>
  uniqueVariables(bodyText(template));

const todayLabel = (now = new Date()) =>
  now.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

/**
 * Smart default for a single variable, mirroring the campaign builder:
 *  - positional {{1}} or a name-ish token  -> contact name
 *  - a date-ish token                       -> today's date
 *  - otherwise                              -> ''
 */
export const computeSmartDefault = (key, { contact = null, now } = {}) => {
  const name =
    contact?.name || contact?.phoneNumber || contact?.phone_number || '';
  const normalized = String(key).trim();

  if (normalized === '1' && name) return name;
  if (NAME_TOKEN_RE.test(normalized) && name) return name;
  if (DATE_TOKEN_RE.test(normalized)) return todayLabel(now);
  return '';
};

/** Build the initial body values map with smart defaults applied. */
export const buildInitialBodyValues = (template, context = {}) => {
  const values = {};
  parseBodyVariables(template).forEach(({ key }) => {
    values[key] = computeSmartDefault(key, context);
  });
  return values;
};

/** Header text variables start empty: `{{1}}` there is rarely the name. */
export const buildInitialHeaderValues = template => {
  const values = {};
  parseHeaderVariables(template).forEach(({ key }) => {
    values[key] = '';
  });
  return values;
};

/** Meta caps a COPY_CODE coupon at 15 characters. */
export const COPY_CODE_MAX_LENGTH = 15;

/**
 * reply-template-picker.TYPES.3 — the value for a dynamic URL button is only
 * the part after the fixed prefix; an agent who pastes the whole link gets the
 * prefix stripped instead of sending it twice.
 */
export const normalizeUrlButtonParam = (button, value) => {
  const text = String(value ?? '').trim();
  const prefix = button?.urlPrefix || '';
  if (prefix && text.startsWith(prefix)) return text.slice(prefix.length);
  return text;
};

/* ------------------------------------------------------------------ *
 * Validation                                                          *
 * ------------------------------------------------------------------ */

/**
 * True when every required variable, media header, and dynamic button
 * parameter has been provided. Gates the Send button.
 */
export const isComposeValid = (
  template,
  bodyValues = {},
  headerMedia = {},
  buttonValues = {},
  headerValues = {}
) => {
  if (!template) return false;
  // whatsapp-flows: the compose step's flow section sets buttonValues.flow
  // ({flowActionData, kaptionFlowId, blocked}) only while the flag is on.
  const flowBinding = buttonValues?.flow;
  if (!isTemplateSendable(template, { allowFlow: !!flowBinding })) return false;
  if (flowBinding?.blocked) return false;

  const filled = value =>
    value !== undefined && value !== null && String(value).trim() !== '';

  const vars = parseBodyVariables(template);
  if (!vars.every(({ key }) => filled(bodyValues[key]))) return false;

  const headerVars = parseHeaderVariables(template);
  if (!headerVars.every(({ key }) => filled(headerValues?.[key]))) return false;

  if (hasMediaHeader(template)) {
    if (!filled(headerMedia?.media_url)) return false;
  }

  const dynamicButtons = getButtonsNeedingParam(template);
  const buttonFilled = b => {
    const value =
      b.paramKind === 'url'
        ? normalizeUrlButtonParam(b, buttonValues[b.index])
        : buttonValues[b.index];
    if (!filled(value)) return false;
    return (
      b.paramKind !== 'copy_code' ||
      String(value).trim().length <= COPY_CODE_MAX_LENGTH
    );
  };
  if (!dynamicButtons.every(buttonFilled)) return false;

  return true;
};

/* ------------------------------------------------------------------ *
 * Rendering + payload                                                 *
 * ------------------------------------------------------------------ */

/** Interpolate the body text with the filled values for the message content. */
export const renderTemplateBody = (template, bodyValues = {}) => {
  const text = bodyText(template);
  return text.replace(/{{\s*([^}]+?)\s*}}/g, (match, token) => {
    const key = token.trim();
    const value = bodyValues[key];
    return value !== undefined && value !== null && String(value) !== ''
      ? String(value)
      : match;
  });
};

/** The TEXT header with its variable filled (for previews). */
export const renderTemplateHeader = (template, headerValues = {}) =>
  getHeaderText(template).replace(VARIABLE_RE, (match, token) => {
    const value = headerValues?.[token.trim()];
    return value !== undefined && value !== null && String(value) !== ''
      ? String(value)
      : match;
  });

/**
 * Assemble the `processed_params` object (body/header/buttons) sent to the
 * backend as part of `template_params`.
 */
export const buildProcessedParams = (
  template,
  bodyValues = {},
  headerMedia = null,
  buttonValues = {},
  headerValues = {}
) => {
  const processed = {};

  const vars = parseBodyVariables(template);
  if (vars.length) {
    processed.body = {};
    vars.forEach(({ key }) => {
      processed.body[key] = bodyValues[key] ?? '';
    });
  }

  if (hasMediaHeader(template)) {
    processed.header = {
      media_url: headerMedia?.media_url || '',
      media_type: mediaHeaderType(template),
    };
    if (mediaHeaderType(template) === 'document') {
      processed.header.media_name = headerMedia?.media_name || '';
    }
    // reply-template-picker.TYPES.2 — the backend re-reads the template's
    // example at send time instead of trusting a link that may have expired.
    if (headerMedia?.source === 'template_example') {
      processed.header.source = 'template_example';
    }
  }

  // reply-template-picker.TYPES.1 — TEXT header variable, keyed like the body.
  const headerVars = parseHeaderVariables(template);
  if (headerVars.length) {
    processed.header = {};
    headerVars.forEach(({ key }) => {
      processed.header[key] = headerValues?.[key] ?? '';
    });
  }

  // Only dynamic buttons (URL-with-vars, COPY_CODE) carry params. Each entry
  // names its button `index`: Rails drops the nulls of a sparse JSON array, so
  // position alone would move a URL button that is not the first one.
  // reply-template-picker.TYPES.4
  const dynamicButtons = getButtonsNeedingParam(template);
  if (dynamicButtons.length) {
    processed.buttons = dynamicButtons.map(b =>
      b.paramKind === 'url'
        ? {
            index: b.index,
            type: 'url',
            parameter: normalizeUrlButtonParam(b, buttonValues[b.index]),
            url: b.url,
            variables: b.variables,
          }
        : {
            index: b.index,
            type: 'copy_code',
            parameter: String(buttonValues[b.index] ?? '').trim(),
          }
    );
  }

  // NOTICE: KAPTION-INTEGRATION - whatsapp-flows: FLOW button entry, by
  // explicit index like the others (prefill + library flow when known).
  const flowIndex = (
    template?.components?.find(c => c.type === 'BUTTONS')?.buttons || []
  ).findIndex(b => String(b.type || '').toUpperCase() === 'FLOW');
  if (flowIndex >= 0 && buttonValues?.flow) {
    const { flowActionData = {}, kaptionFlowId = null } = buttonValues.flow;
    const data = Object.fromEntries(
      Object.entries(flowActionData).filter(
        ([, v]) => v !== undefined && v !== null && String(v).trim() !== ''
      )
    );
    const entry = { index: flowIndex, type: 'flow' };
    if (kaptionFlowId) entry.kaption_flow_id = kaptionFlowId;
    if (Object.keys(data).length) entry.flow_action_data = data;
    processed.buttons = [...(processed.buttons || []), entry];
  }

  return processed;
};

/** Full `template_params` payload for the create-conversation message. */
export const buildTemplateParams = (
  template,
  bodyValues = {},
  headerMedia = null,
  buttonValues = {},
  headerValues = {}
) => ({
  name: template?.name,
  category: template?.category,
  language: template?.language,
  namespace: template?.namespace,
  processed_params: buildProcessedParams(
    template,
    bodyValues,
    headerMedia,
    buttonValues,
    headerValues
  ),
});

/**
 * Body-variable map keyed by index for the Kaption Scheduled Messages API
 * (`variables: { "1": "value" }`). The scheduled-message job resolves the
 * template body from this map at delivery time.
 */
export const buildScheduleVariables = (template, bodyValues = {}) => {
  const map = {};
  parseBodyVariables(template).forEach(({ key }) => {
    map[key] = bodyValues[key] ?? '';
  });
  return map;
};

/** Payload for POST .../kaption_scheduled_messages (a template scheduled send). */
export const buildScheduledMessagePayload = ({
  template,
  bodyValues = {},
  scheduledAt,
  timezone,
}) => ({
  scheduled_at: scheduledAt,
  timezone,
  message_type: 'template',
  template_name: template?.name,
  variables: buildScheduleVariables(template, bodyValues),
});

/**
 * Build the exact payload dispatched to `contactConversations/create` for a
 * WhatsApp template message. Mirrors `prepareWhatsAppMessagePayload` so the
 * create-on-send path reuses the existing conversations endpoint contract.
 */
export const buildWhatsAppConversationPayload = ({
  inbox,
  contact,
  sourceId,
  content,
  templateParams,
  currentUser,
}) => ({
  inboxId: inbox?.id,
  sourceId: sourceId ?? inbox?.sourceId,
  contactId: contact?.id,
  message: { content, template_params: templateParams },
  assigneeId: currentUser?.id,
});
